import { ChatMessageData } from "@services/models/Prime/ChatHistoryModel";
import { formatDisplayDate } from "@utils/safeDate";
import React from "react";
import ReactMarkdown from "react-markdown";

interface MessageBubbleProps {
  chatMessageData: ChatMessageData;
  userId: number;
  className?: string;
  onClick?: () => void;
}

const MessageBubble: React.FC<MessageBubbleProps> = ({
  chatMessageData,
  userId,
  className = "",
  onClick,
}) => {
  const isCurrentUser = chatMessageData.sender.id === userId;

  const formatTime = (dateString: string) =>
    formatDisplayDate(dateString, undefined, {
      hour: "2-digit",
      minute: "2-digit",
    });

  const getFileNameFromUrl = (url: string) => {
    try {
      const urlParts = url.split("/");
      return urlParts[urlParts.length - 1];
    } catch {
      return "Document";
    }
  };

  const processMessageContent = (content: string) => {
    // Replace literal "\n" with actual newlines
    return content.replace(/\\n/g, "\n");
  };

  const renderContent = () => {
    switch (chatMessageData.messageType) {
      case "image":
        return (
          <img
            src={chatMessageData.messageContent}
            alt="Sent Image"
            className="max-w-full rounded-[8px] shadow-md cursor-pointer"
            onClick={onClick}
          />
        );

      case "video":
        return (
          <video
            src={chatMessageData.messageContent}
            controls
            className="max-w-full rounded-[8px] shadow-md cursor-pointer"
            onClick={onClick}
          />
        );

      case "document":
        return (
          <a
            href={chatMessageData.messageContent}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full"
          >
            <div className="flex items-center gap-3 p-3 bg-bg-page-body rounded-lg border border-border-base transition-colors duration-200">
              <div className="flex-shrink-0">
                <span className="material-symbols-outlined text-text-base text-[32px]">
                  description
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-medium text-text-base truncate">
                  {getFileNameFromUrl(chatMessageData.messageContent)}
                </p>
                <p className="text-[12px] text-text-subtle">Click to open</p>
              </div>
            </div>
          </a>
        );

      default:
        const processedContent = processMessageContent(
          chatMessageData.messageContent
        );
        if (chatMessageData.sender.id === 0) {
          return (
            <ReactMarkdown
              components={{
                ol: ({ children }) => (
                  <ol
                    style={{
                      listStyleType: "decimal",
                      paddingLeft: "1.5rem",
                    }}
                  >
                    {children}
                  </ol>
                ),
                li: ({ children }) => (
                  <li style={{ margin: "0.25rem 0" }}>{children}</li>
                ),
              }}
              //className="text-[12px] text-text-base font-normal opacity-70"
            >
              {processedContent}
            </ReactMarkdown>
          );
        } else {
          return (
            <p className="text-[12px] text-text-base font-normal opacity-70 whitespace-pre-wrap">
              {chatMessageData.messageContent}
            </p>
          );
        }
    }
  };

  return (
    <div
      key={chatMessageData.messageId}
      className={`flex flex-col ${
        isCurrentUser
          ? "items-end"
          : chatMessageData.sender.id === 0
          ? "items-center"
          : "items-start"
      } mb-4`}
    >
      <div
        className={`
         ${chatMessageData.sender.id === 0 ? "w-full" : "max-w-[80%]"} 
          px-[12px] py-[16px]
          rounded-[8px] 
          text-text-base 
          text-[12px] 
          font-normal 
          ${
            isCurrentUser
              ? "bg-bg-helper-green-light"
              : chatMessageData.sender.id === 0 ||
                chatMessageData.sender.id === 1
              ? "bg-bg-page-body"
              : "bg-bg-base"
          } 
          border 
         ${
           chatMessageData.sender.id === 0 || chatMessageData.sender.id === 1
             ? "border-border-button-primary"
             : "border-border-subtle"
         } 
          ${className}
        `}
      >
        <div className="flex flex-row items-center justify-between mb-[8px]">
          <span
            className={`  ${
              chatMessageData.sender.id === 0 || chatMessageData.sender.id === 1
                ? "text-text-button-tertiary"
                : "text-text-base"
            }  text-[12px] font-medium mr-[8px]`}
          >
            {chatMessageData.sender.id === 0
              ? "Service Message"
              : chatMessageData.sender.id === 1
              ? "Admin"
              : chatMessageData.sender.username}
          </span>
          <span className="text-text-base text-[10px] font-normal">
            {formatTime(chatMessageData.createdAt)}
          </span>
        </div>
        {renderContent()}
      </div>
    </div>
  );
};

export default MessageBubble;
